import {
  SetpointProfile,
  CreateOneSetpointProfileInput,
  UpdateOneSetpointProfileInput,
  useUpdateSetpointProfileMutation,
  useCreateSetpointProfileMutation,
} from '../../../../types/generated-types';
import React, { ReactElement, useCallback, useState } from 'react';
import { FormValues } from '../types/setpointLimitProfileDetailFormValues';
import { Form, Formik, FormikHelpers, useFormik } from 'formik';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { SetpointLimitProfileGeneralTab } from '../components/setpoint-limit-profile-detail-general-tab';
import { SetpointLimitProfilePropertiesTab } from '../components/setpoint-limit-profile-detail-properties-tab';
import { useNavigate } from 'react-router-dom';
import { ActivityIndicator } from '../../shared/activityIndicator';
import { defaultAbsoluteLimits } from '../../helpers';
import { AuthorizedUser } from '../../../auth/models';
import { convertLimitTemperature } from '../../devices/util/convert-temp-units';
import { Notifier } from '../../../system/services/notificationManager';
import {
  Celsius,
  TemperatureUnit,
} from '../../../system/models/temperatureUnits';
import { BulkUpdateStatus } from '../../bulkConfig/common/bulk-update-status';
import { VerifySetpointProfileUpdate } from '../components/verify-setpoint-profile-update';
import { Button } from '@mui/material';

const tabStyles = {
  error: {
    color: 'red',
    fontWeight: '600',
  },
  normal: {
    fontWeight: 'inherit',
  },
};
type FormVariant = 'general' | 'property' | 'unit';

export const SimpleSetpointProfileForm = ({
  profile,
  user,
  currentUnits = 'C',
  variant,
  onClose = undefined,
}: {
  profile: Partial<SetpointProfile>;
  user: AuthorizedUser;
  currentUnits?: TemperatureUnit;
  variant?: FormVariant;
  onClose?: (id?: string, refetch?: boolean) => void;
}): ReactElement => {
  const navigator = useNavigate();
  const [value, setValue] = React.useState<number>(0);
  const [propagating, setPropagating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationReady, setVerificationReady] = useState(false);

  const [preferredUnits] = useState<TemperatureUnit>(user.preferredUnits);
  const templateSeeds = profile._id ? undefined : defaultAbsoluteLimits.C;
  const defaultLimits = {
    heat: {
      min: convertLimitTemperature(templateSeeds?.heat.min, preferredUnits),
      max: convertLimitTemperature(templateSeeds?.heat.max, preferredUnits),
    },
    cool: {
      min: convertLimitTemperature(templateSeeds?.cool.min, preferredUnits),
      max: convertLimitTemperature(templateSeeds?.cool.max, preferredUnits),
    },
  };

  const [updateSetpointProfile] = useUpdateSetpointProfileMutation();

  const [createSetpointProfile] = useCreateSetpointProfileMutation();

  const getActions = (variant?: FormVariant) => {
    switch (variant) {
      case 'property':
        return [
          {
            label: 'Back To Property SPLP Editor',
            handler: () => {
              setPropagating(false);
              formik.resetForm({ values: formik.values });
            },
          },
          {
            label: 'Back To Property Detail',
            handler: () => {
              console.log('[splp-detail-form][handler]');
              onClose && onClose(undefined, false);
            },
          },
        ];
      case 'unit':
        return [
          {
            label: 'Back To Unit SPLP Editor',
            handler: () => {
              setPropagating(false);
              formik.resetForm({ values: formik.values });
            },
          },
          {
            label: 'Back To Unit Detail',
            handler: () => {
              console.log('[splp-detail-form][handler]');
              onClose && onClose(undefined, false);
            },
          },
        ];
      case 'general':
      default:
        return [
          {
            label: 'Return to Setpoint Profile List',
            handler: () => navigator('/setpoint-profiles'),
          },
          {
            label: 'Return to editing',
            handler: () => {
              setPropagating(false);
              formik.resetForm({ values: formik.values });
            },
          },
        ];
    }
  };

  const initialValues: FormValues = {
    _id: profile._id ?? '',
    name: profile.name ?? '',
    maxHeatSetpointLimit:
      convertLimitTemperature(
        profile.heat?.max,
        preferredUnits,
        currentUnits,
      ) ?? defaultLimits.heat.max,
    minHeatSetpointLimit:
      convertLimitTemperature(
        profile.heat?.min,
        preferredUnits,
        currentUnits,
      ) ?? defaultLimits.heat.min,
    maxCoolSetpointLimit:
      convertLimitTemperature(
        profile.cool?.max,
        preferredUnits,
        currentUnits,
      ) ?? defaultLimits.cool.max,
    minCoolSetpointLimit:
      convertLimitTemperature(
        profile.cool?.min,
        preferredUnits,
        currentUnits,
      ) ?? defaultLimits.cool.min,
    properties: (profile.properties as string[]) ?? [],
  };

  const onSubmit = (
    values: FormValues,
    _helpers: FormikHelpers<FormValues>,
  ) => {
    if (profile._id) {
      setPropagating(true);
      setVerifying(false);
      setVerificationReady(false);
      const profileUpdateFields: UpdateOneSetpointProfileInput = {
        name: values.name,
        heat: {
          max: convertLimitTemperature(
            values.maxHeatSetpointLimit,
            Celsius,
            preferredUnits,
          ),
          min: convertLimitTemperature(
            values.minHeatSetpointLimit,
            Celsius,
            preferredUnits,
          ),
        },
        cool: {
          max: convertLimitTemperature(
            values.maxCoolSetpointLimit,
            Celsius,
            preferredUnits,
          ),
          min: convertLimitTemperature(
            values.minCoolSetpointLimit,
            Celsius,
            preferredUnits,
          ),
        },
        properties: values.properties,
      };

      updateSetpointProfile({
        variables: {
          id: profile._id,
          profile: profileUpdateFields,
        },
      })
        .then(() => {
          formik.setSubmitting(false);
          Notifier.success('Setpoint Limit Profile updated');
        })
        .catch((updateError) => {
          formik.setSubmitting(false);
          Notifier.error(updateError.message);
          setPropagating(false);
        });
    } else {
      const profileFields: CreateOneSetpointProfileInput = {
        name: values.name,
        heat: {
          max: convertLimitTemperature(
            values.maxHeatSetpointLimit,
            Celsius,
            preferredUnits,
          ),
          min: convertLimitTemperature(
            values.minHeatSetpointLimit,
            Celsius,
            preferredUnits,
          ),
        },
        cool: {
          max: convertLimitTemperature(
            values.maxCoolSetpointLimit,
            Celsius,
            preferredUnits,
          ),
          min: convertLimitTemperature(
            values.minCoolSetpointLimit,
            Celsius,
            preferredUnits,
          ),
        },
        properties: values.properties,
      };

      createSetpointProfile({
        variables: {
          profile: profileFields,
        },
      })
        .then((res) => {
          formik.setSubmitting(false);
          Notifier.success('Setpoint Limit Profile created');
          if (onClose) {
            onClose(res?.data?.createSetpointProfile?._id);
          } else {
            navigator('/setpoint-profiles');
          }
        })
        .catch((createError) => {
          formik.setSubmitting(false);
          Notifier.error(createError.message);
        });
    }
  };

  const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    properties: yup.array(),
    minHeatSetpointLimit: yup.number().required(),
    maxHeatSetpointLimit: yup
      .number()
      .required()
      .min(
        yup.ref('minHeatSetpointLimit'),
        'Heating maximum must be at least its minimum',
      ),
    minCoolSetpointLimit: yup.number().required(),
    maxCoolSetpointLimit: yup
      .number()
      .required()
      .min(
        yup.ref('minCoolSetpointLimit'),
        'Cooling maximum must be at least its minimum',
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const handleChange = useCallback(
    (changes: Partial<FormValues>) => {
      formik.setValues({ ...formik.values, ...changes });
    },
    [formik],
  );

  const handleSetpointLimitChange = useCallback(
    (key: string, value: number) => {
      if (!Number.isFinite(value)) return {};
      // Profiles are numerical templates. Do not clamp them to one equipment
      // family or change another endpoint while the user enters policy intent.
      const changes = { [key]: value };
      handleChange(changes);
      return changes;
    },
    [handleChange],
  );

  return user ? (
    <div style={{ padding: '6px', paddingBottom: '80px' }}>
      <h2>
        {profile._id ? 'Edit' : 'Create'} Setpoint Profile
        {formik.values.name ? `: ${formik.values.name}` : ''}
      </h2>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        <Form
          style={{ width: '100%', fontSize: '16px' }}
          onSubmit={formik.handleSubmit}
        >
          {propagating ? (
            <div>
              <ActivityIndicator activityInProgress={formik.isSubmitting} />
              <BulkUpdateStatus
                title="Setpoint Profile Propagation Status"
                initiator={profile}
                completeCallback={() => {
                  setPropagating(false);
                }}
                actions={getActions(variant)}
              />
            </div>
          ) : (
            <div>
              {verifying ? (
                <div
                  style={{
                    marginBottom: '6px',
                    paddingBottom: '6px',
                    borderBottom: '1px solid lightgray',
                  }}
                >
                  <VerifySetpointProfileUpdate
                    values={formik.values}
                    initialValues={initialValues}
                    dataReadyCallback={setVerificationReady}
                  />
                </div>
              ) : (
                <div>
                  {!onClose ? (
                    <Box
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        marginBottom: '18px',
                      }}
                    >
                      <Tabs
                        value={value}
                        TabIndicatorProps={{
                          style: { color: '#000' },
                        }}
                        indicatorColor="secondary"
                        textColor="secondary"
                        onChange={(
                          event: React.SyntheticEvent,
                          newValue: number,
                        ) => {
                          if (!propagating) {
                            setValue(newValue);
                          }
                        }}
                        aria-label="View or Edit Setpoint Limit Profile"
                      >
                        <Tab
                          label="General"
                          style={
                            formik.errors.name ||
                            formik.errors.maxHeatSetpointLimit ||
                            formik.errors.minHeatSetpointLimit ||
                            formik.errors.maxCoolSetpointLimit ||
                            formik.errors.minCoolSetpointLimit
                              ? tabStyles.error
                              : tabStyles.normal
                          }
                          id="setpoint-profile-general-tab"
                        />
                        <Tab
                          label="Properties"
                          style={
                            formik.errors.properties
                              ? tabStyles.error
                              : tabStyles.normal
                          }
                          id="setpoint-profile-properties-tab"
                        />
                      </Tabs>
                    </Box>
                  ) : null}
                  {value === 0 ? (
                    <SetpointLimitProfileGeneralTab
                      formik={formik}
                      onChange={(e) => {
                        formik.setFieldTouched((e.target as any).id, true);
                        formik.handleChange(e);
                      }}
                      handleChange={handleChange}
                      handleSetpointLimitChange={handleSetpointLimitChange}
                      targetUnits={preferredUnits}
                      isSuper={user.isSuper}
                    />
                  ) : (
                    <SetpointLimitProfilePropertiesTab formik={formik} />
                  )}
                </div>
              )}
              <br />
              {verifying && verificationReady ? (
                <Button
                  disabled={!formik.isValid || !formik.dirty}
                  type="submit"
                  variant="contained"
                  color="secondary"
                  sx={{ color: '#fff' }}
                >
                  Commit Changes
                </Button>
              ) : (
                <Button
                  disabled={!formik.isValid || !formik.dirty}
                  type="submit"
                  variant="contained"
                  color="secondary"
                  sx={{ color: '#fff' }}
                  onClick={(e) => {
                    if (formik.values._id) {
                      e.preventDefault();
                      e.stopPropagation();
                      setVerifying(true);
                    }
                  }}
                >
                  Save
                </Button>
              )}
              <Button
                sx={{ marginLeft: '10px' }}
                variant="text"
                color={
                  !formik.isValid || !formik.dirty ? 'secondary' : 'primary'
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (verifying) {
                    setVerifying(false);
                    setVerificationReady(false);
                  } else {
                    if (onClose) {
                      onClose();
                    } else {
                      navigator('/setpoint-profiles');
                    }
                  }
                }}
              >
                Close
              </Button>
            </div>
          )}
        </Form>
      </Formik>
    </div>
  ) : (
    <div>Unauthorized</div>
  );
};
